import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { heroContent, bimHighlights } from "@/mock/siteContent";

const API = process.env.REACT_APP_BACKEND_URL;

const featureCards = [
  {
    num: "01",
    tag: "Escritórios e projetistas",
    title: "Projetos e modelagem BIM disciplinar",
    description: "Modelos de arquitetura, estrutura e MEP prontos para documentação, quantitativos e revisão técnica.",
    cta: "Saber mais",
  },
  {
    num: "02",
    tag: "Construtoras e incorporadoras",
    title: "Coordenação e compatibilização BIM",
    description: "Organização de modelos, detecção de interferências e relatórios para reduzir retrabalho em obra.",
    cta: "Saber mais",
  },
  {
    num: "03",
    tag: "Escritórios e times internos",
    title: "Implantação de processos e padrões BIM",
    description: "Fluxos, templates, bibliotecas e critérios de LOD para tornar o BIM parte do dia a dia.",
    cta: "Saber mais",
  },
];

const stats = [
  { value: "LOD 100–400", label: "Níveis de detalhamento" },
  { value: "MEP + ARQ + EST", label: "Disciplinas atendidas" },
  { value: "IFC / RVT", label: "Formatos de entrega" },
  { value: "Canaã – PA", label: "Base de operações" },
];

const Home = () => {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/projects/featured`)
      .then(r => r.json())
      .then(data => setFeatured(data))
      .catch(() => {});
  }, []);

  const projectImages = featured.filter(p => p.images && p.images.length > 0).flatMap(p =>
    p.images.map((src, i) => ({ src, label: i === 0 ? `${p.name.split("–")[0].trim()}` : `${p.name.split("–")[0].trim()} · Vista ${i + 1}`, segment: p.segment }))
  );

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" style={{
        minHeight: "calc(100vh - 64px)",
        display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
        textAlign: "center", padding: "80px 24px 60px", position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: "30%", left: "50%", transform: "translate(-50%, -50%)",
          width: 600, height: 600,
          background: "radial-gradient(circle, rgba(212,160,74,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        <div className="fade-up-1" style={{
          fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.22em",
          color: "var(--gold)", textTransform: "uppercase",
          display: "flex", alignItems: "center", gap: 10, marginBottom: 24,
        }}>
          <span style={{ width: 24, height: 1, background: "var(--gold)", display: "inline-block" }} />
          {heroContent.eyebrow}
          <span style={{ width: 24, height: 1, background: "var(--gold)", display: "inline-block" }} />
        </div>
        <h1 className="fade-up-2" data-testid="hero-title" style={{
          fontFamily: "'Syne', sans-serif", fontWeight: 600,
          fontSize: "clamp(2.2rem, 5vw, 4rem)", lineHeight: 1.1,
          letterSpacing: "-0.01em", color: "var(--text)", maxWidth: 760, marginBottom: 20,
        }}>
          {heroContent.title}
        </h1>
        <p className="fade-up-3" style={{
          fontFamily: "'Instrument Sans', sans-serif",
          fontSize: "clamp(0.95rem, 2vw, 1.1rem)",
          color: "var(--muted)", maxWidth: 480, lineHeight: 1.65, marginBottom: 40,
        }}>
          {heroContent.subtitle}
        </p>
        <div className="fade-up-4 hero-buttons" style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center" }}>
          <a
            href="https://wa.me/5594991146693?text=Ol%C3%A1%2C%20tenho%20interesse%20em%20projetos%20BIM%20e%20gostaria%20de%20falar%20com%20um%20especialista%20para%20tirar%20algumas%20d%C3%BAvidas"
            target="_blank" rel="noopener noreferrer" className="btn-gold"
            data-testid="hero-cta-whatsapp"
          >
            {heroContent.primaryCta}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
          <Link to="/portfolio" className="btn-ghost" data-testid="hero-cta-portfolio">{heroContent.secondaryCta}</Link>
        </div>
        <div style={{
          position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6, opacity: 0.35,
        }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.2em", color: "var(--muted)", textTransform: "uppercase" }}>scroll</span>
          <div style={{ width: 1, height: 36, background: "linear-gradient(to bottom, var(--muted), transparent)" }} />
        </div>
      </section>

      {/* STATS BAR */}
      <section data-testid="stats-section" style={{ borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: "var(--surface)", padding: "0 24px" }}>
        <div className="grid-stats" style={{ maxWidth: 1140, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>
          {stats.map((s, i) => (
            <div key={i} style={{
              padding: "24px 20px",
              borderRight: i < 3 ? "1px solid var(--border)" : "none",
              textAlign: "center",
            }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: "1rem", color: "var(--gold)", letterSpacing: "0.04em", marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.12em", color: "var(--muted2)", textTransform: "uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MARQUEE */}
      <section style={{ overflow: "hidden", borderBottom: "1px solid var(--border)", background: "var(--bg)", padding: "10px 0" }}>
        <div style={{ display: "flex", width: "100%", overflow: "hidden" }}>
          <div className="animate-marquee" style={{ whiteSpace: "nowrap" }}>
            {bimHighlights.concat(bimHighlights).concat(bimHighlights).map((item, i) => (
              <span key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 10, margin: "0 28px",
                fontFamily: "'DM Mono', monospace", fontSize: "0.65rem",
                letterSpacing: "0.1em", color: "var(--muted2)", textTransform: "uppercase",
              }}>
                <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--gold)", display: "inline-block", flexShrink: 0 }} />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PROJETOS */}
      <section data-testid="projects-section" style={{ maxWidth: 1140, margin: "0 auto", padding: "80px 24px" }}>
        <div style={{ marginBottom: 48 }}>
          <p style={{
            fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.2em",
            color: "var(--gold)", textTransform: "uppercase", marginBottom: 10,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ width: 20, height: 1, background: "var(--gold)", display: "inline-block" }} />
            Modelos reais
          </p>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "var(--text)", letterSpacing: "-0.01em" }}>
            Projetos desenvolvidos
          </h2>
        </div>
        {projectImages.length > 0 ? (
          <div className="grid-projects-home" style={{ display: "grid", gridTemplateColumns: projectImages.length >= 3 ? "repeat(3, 1fr)" : "repeat(2, 1fr)", gap: 2 }}>
            {projectImages.slice(0, 6).map(({ src, label }, idx) => (
              <div key={idx} style={{ position: "relative", overflow: "hidden", aspectRatio: "16/10", background: "var(--surface)" }}>
                <img src={src} alt={label}
                  style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s" }}
                  onMouseEnter={e => e.target.style.transform = "scale(1.04)"}
                  onMouseLeave={e => e.target.style.transform = "scale(1)"}
                />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(9,9,11,0.85) 0%, transparent 55%)" }} />
                <div style={{ position: "absolute", bottom: 16, left: 16 }}>
                  <span className="tag">{label}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 40, color: "var(--muted2)" }}>Carregando projetos...</div>
        )}
        <div style={{ textAlign: "center", marginTop: 32 }}>
          <Link to="/portfolio" className="btn-ghost">Ver todos os projetos</Link>
        </div>
      </section>

      {/* SOLUÇÕES */}
      <section data-testid="solutions-section" style={{ borderTop: "1px solid var(--border)", background: "var(--surface)", padding: "80px 24px" }}>
        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
          <div className="solutions-header" style={{ marginBottom: 52, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div>
              <p style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 20, height: 1, background: "var(--gold)", display: "inline-block" }} />
                Soluções
              </p>
              <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "var(--text)", letterSpacing: "-0.01em", maxWidth: 480 }}>
                Onde a BIMtec gera resultado
              </h2>
            </div>
            <Link to="/contato" className="btn-ghost" style={{ flexShrink: 0 }}>Falar com especialista</Link>
          </div>
          <div className="grid-solutions" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: "var(--border)" }}>
            {featureCards.map((card) => (
              <div key={card.num} className="card-hover" style={{ background: "var(--bg)", padding: "32px 28px", border: "1px solid transparent", cursor: "default" }}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.18em", color: "var(--gold)", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
                  {card.num}
                  <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
                  <span style={{ color: "var(--muted2)", textTransform: "uppercase", fontSize: "0.58rem" }}>{card.tag}</span>
                </div>
                <h3 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: "1rem", color: "var(--text)", marginBottom: 14, lineHeight: 1.3 }}>{card.title}</h3>
                <p style={{ fontSize: "0.85rem", color: "var(--muted)", lineHeight: 1.65, marginBottom: 28 }}>{card.description}</p>
                <Link to="/contato" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, transition: "gap 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.gap = "10px"}
                  onMouseLeave={e => e.currentTarget.style.gap = "6px"}
                >
                  {card.cta}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" style={{ borderTop: "1px solid var(--border)", padding: "64px 24px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 500, height: 200, background: "radial-gradient(ellipse, rgba(212,160,74,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 600, margin: "0 auto", position: "relative" }}>
          <p style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 16 }}>Pronto para começar?</p>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: "clamp(1.5rem, 3vw, 2.2rem)", color: "var(--text)", marginBottom: 28, letterSpacing: "-0.01em" }}>Fale com nossa equipe BIM</h2>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="https://wa.me/5594991146693?text=Ol%C3%A1%2C%20tenho%20interesse%20em%20projetos%20BIM%20e%20gostaria%20de%20falar%20com%20um%20especialista%20para%20tirar%20algumas%20d%C3%BAvidas" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="cta-whatsapp">WhatsApp</a>
            <Link to="/contato" className="btn-ghost" data-testid="cta-contact">Formulário de contato</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
